import { TooltipPortal } from '@radix-ui/react-tooltip';
import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/shared/components/ui/alerts/tooltip';
import DynamicIcon, { Icons } from 'src/shared/components/ui/icons/dynamic-icon';
import { cn } from 'src/shared/lib/utils';

type TagNameBadgeProps = {
  icon?: Icons;
  tagName: string;
  size?: 'sm' | 'xs';
  isPrimitive: boolean;
  className?: string;
  verified?: boolean;
  children?: ReactNode;
};

export default function TagNameBadge({
  size = 'sm',
  tagName,
  icon,
  className,
  verified,
  children,
  isPrimitive,
}: TagNameBadgeProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    if (textRef.current) {
      // Compare the text element's full width vs its visible width
      const isTextOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
      setIsOverflowing(isTextOverflowing);
    }
  }, []);

  useLayoutEffect(() => {
    checkOverflow();
    // Add resize listener for responsive layouts
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [tagName, checkOverflow]);

  const shouldShowTooltip = isOverflowing || verified;

  return (
    <Tooltip>
      <TooltipTrigger
        className={cn(
          'rounded-sm w-fit max-w-48 px-1.5 py-1 gap-2 flex items-center font-normal bg-blue-50 text-primary min-h-5',
          size === 'sm' && 'px-2 min-h-7 text-sm',
          size === 'xs' && 'px-1 min-h-5 text-xs',
          !isPrimitive && 'bg-transparent text-black hover:bg-neutral-100',
          verified && 'bg-green-600/10 text-green-600',
          className
        )}
      >
        {icon && <DynamicIcon name={icon} size={size === 'sm' ? 12 : 10} className="shrink-0" />}
        <span ref={textRef} className="truncate">
          {tagName}
        </span>
        {children && <div className="shrink-0 ml-auto">{children}</div>}
      </TooltipTrigger>
      {shouldShowTooltip && (
        <TooltipPortal>
          <TooltipContent>{verified ? `Verified - ${tagName}` : tagName}</TooltipContent>
        </TooltipPortal>
      )}
    </Tooltip>
  );
}
